import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout, { Card, Label, Badge, Spinner } from "../components/Layout";
import { Plus, ShieldCheck, CheckCircle, XCircle, Trash, Lightning } from "@phosphor-icons/react";

export default function AdminPortals() {
  const [catalog, setCatalog] = useState([]);
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const [{ data: c }, { data: r }] = await Promise.all([
      api.get("/portals/catalog"),
      api.get("/portals/credentials"),
    ]);
    setCatalog(c);
    setCreds(r);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const test = async (cid) => {
    try {
      const { data } = await api.post(`/portals/credentials/${cid}/test`);
      alert(`Healthcheck: ${data.status} (${data.portal})`);
      load();
    } catch (e) { alert("Errore test: " + e.message); }
  };

  const del = async (cid) => {
    if (!confirm("Eliminare la credenziale?")) return;
    await api.delete(`/portals/credentials/${cid}`);
    load();
  };

  const portalMap = Object.fromEntries(catalog.map(p => [p.code, p]));
  const configured = Object.fromEntries(creds.map(c => [c.portal_code, c]));

  return (
    <Layout
      title="Admin / Portali"
      subtitle="Configurazione credenziali e scouting automatico"
      actions={<button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid="btn-add-portal"><Plus size={14} className="inline mr-1.5" /> Aggiungi credenziale</button>}
    >
      <Card className="mb-4 ai-panel">
        <div className="flex items-start gap-4">
          <ShieldCheck weight="duotone" className="text-violet-600 flex-shrink-0" size={24} />
          <div>
            <Label>Sicurezza</Label>
            <p className="text-sm text-zinc-700 mt-1">
              Le password vengono cifrate at-rest con AES (Fernet/cryptography). Mai loggate in chiaro.
              Healthcheck periodico (12h) per validare la persistenza delle credenziali.
            </p>
          </div>
        </div>
      </Card>

      {loading ? <Spinner /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portals-grid">
          {catalog.map((p) => {
            const cred = configured[p.code];
            const isConfigured = !!cred;
            return (
              <Card key={p.code} data-testid={`portal-${p.code}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-start gap-2">
                    <div className={`w-10 h-10 ${isConfigured ? "bg-emerald-100" : "bg-zinc-100"} rounded-sm flex items-center justify-center flex-shrink-0`}>
                      {isConfigured ? <CheckCircle weight="fill" className="text-emerald-600" size={20} /> : <XCircle className="text-zinc-400" size={20} />}
                    </div>
                    <div>
                      <h3 className="font-display font-bold tracking-tight">{p.name}</h3>
                      <div className="text-xs text-zinc-500 font-mono">{p.code} • {p.type}</div>
                    </div>
                  </div>
                </div>

                {isConfigured ? (
                  <div className="space-y-2">
                    <div className="text-xs text-zinc-600">
                      <span className="text-zinc-400">User:</span> <span className="font-mono">{cred.username}</span>
                    </div>
                    <div className="text-xs text-zinc-600">
                      <span className="text-zinc-400">MFA:</span> {cred.mfa_method}
                    </div>
                    {cred.last_check_at && (
                      <div className="text-xs">
                        <Badge kind={cred.last_check_status === "OK" ? "aperta" : "compromessa"}>
                          {cred.last_check_status} • {new Date(cred.last_check_at).toLocaleString("it-IT")}
                        </Badge>
                      </div>
                    )}
                    {cred.auto_scouting && <Badge kind="aperta">Scouting auto attivo</Badge>}
                    <div className="flex gap-2 pt-2 border-t border-zinc-100 mt-3">
                      <button onClick={() => test(cred.id)} className="btn-secondary text-xs" data-testid={`test-${p.code}`}>
                        <Lightning size={11} className="inline mr-1" /> Test
                      </button>
                      <button onClick={() => { setEditing({ ...cred, portal_code: p.code }); setShowForm(true); }} className="btn-secondary text-xs" data-testid={`edit-${p.code}`}>Modifica</button>
                      <button onClick={() => del(cred.id)} className="text-red-600 hover:underline text-xs" data-testid={`del-${p.code}`}><Trash size={12} className="inline" /></button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-zinc-500 mb-3">Nessuna credenziale configurata. Lo scouting su questo portale non e attivo.</p>
                    <button onClick={() => { setEditing({ portal_code: p.code }); setShowForm(true); }} className="btn-primary text-xs w-full" data-testid={`configure-${p.code}`}>
                      Configura
                    </button>
                  </div>
                )}
                <a href={p.url} target="_blank" rel="noreferrer" className="text-[10px] text-zinc-400 hover:underline mt-2 inline-block">{p.url}</a>
              </Card>
            );
          })}
        </div>
      )}

      {showForm && <CredentialForm initial={editing} catalog={catalog} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </Layout>
  );
}

function CredentialForm({ initial, catalog, onClose, onSaved }) {
  const [f, setF] = useState({
    portal_code: initial?.portal_code || catalog[0]?.code || "CONSIP",
    username: initial?.username || "",
    password: "",
    token: "",
    mfa_method: initial?.mfa_method || "NONE",
    auto_scouting: initial?.auto_scouting || false,
  });
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/portals/credentials", f);
      onSaved();
    } catch (e2) { alert("Errore: " + (e2.response?.data?.detail || e2.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="cred-form">
      <form onSubmit={submit} className="bg-white border border-zinc-300 w-full max-w-lg p-6 space-y-4">
        <h2 className="font-display text-2xl font-extrabold tracking-tighter">Credenziale Portale</h2>
        <div>
          <div className="label-up mb-1">Portale</div>
          <select value={f.portal_code} onChange={upd("portal_code")} disabled={!!initial?.portal_code && initial?.username} className="input-field" data-testid="form-portal">
            {catalog.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-up mb-1">Username</div>
          <input required value={f.username} onChange={upd("username")} className="input-field" data-testid="form-username" />
        </div>
        <div>
          <div className="label-up mb-1">Password {initial?.username && "(lascia vuoto per non cambiare)"}</div>
          <input type="password" required={!initial?.username} value={f.password} onChange={upd("password")} className="input-field" data-testid="form-password" />
        </div>
        <div>
          <div className="label-up mb-1">Token API (opzionale)</div>
          <input value={f.token} onChange={upd("token")} className="input-field font-mono text-xs" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="label-up mb-1">Metodo MFA</div>
            <select value={f.mfa_method} onChange={upd("mfa_method")} className="input-field">
              <option>NONE</option><option>OTP_EMAIL</option><option>OTP_APP</option><option>CNS</option><option>SPID</option>
            </select>
          </div>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={f.auto_scouting} onChange={upd("auto_scouting")} data-testid="form-auto" />
            <span className="text-sm">Scouting automatico</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
          <button type="button" onClick={onClose} className="btn-secondary">Annulla</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="form-save">{saving ? "Salvataggio..." : "Salva"}</button>
        </div>
      </form>
    </div>
  );
}
